/**
 * Threedium 3D Experience Modal
 * Handles 3D iframe modal functionality for product pages
 */

(function() {
  'use strict';

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    init3DModal();
  });

  // Bridge state shared across the modal lifecycle.
  var Bridge = {
    targetOrigin: null,
    iframeEl: null,
    messageHandler: null,
    variantChangeHandler: null
  };

  function init3DModal() {
    // Find all custom 3D buttons on the page
    var customButtons = document.querySelectorAll('.custom-carousel-button');

    if (customButtons.length === 0) {
      return; // No 3D buttons found, exit early
    }

    // Create modal structure
    var modal = createModal();

    // Bind event listeners to each button
    customButtons.forEach(function(button) {
      bindButtonEvents(button, modal);
    });
  }

  // Read the JSON blob rendered by snippets/threedium-product-data.liquid.
  function readProductData() {
    var el = document.getElementById('threedium-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('threedium: failed to parse product data', err);
      return null;
    }
  }

  function readGlobaleCurrency() {
    try {
      var code = window.glbeApp && window.glbeApp.currencyCode;
      return typeof code === 'string' && code.length ? code : null;
    } catch (e) {
      return null;
    }
  }

  // Read the user's currently-selected monogram values from the existing
  // monogram form fields (kept in sync by assets/custom-monogram.js).
  function readMonogramSelection() {
    var textEl = document.querySelector('.monogram__input-field');
    var styleEl = document.querySelector('input[name="monogram-style"]:checked');
    var colorEl = document.querySelector('input[name="monogram-color"]:checked');
    return {
      text: textEl ? textEl.value : '',
      style: styleEl ? styleEl.value : null,
      color: colorEl ? colorEl.value : null
    };
  }

  // Resolve the exact variant the user has selected by reading the checked
  // radio buttons on the PDP and matching against the full variant list in
  // the JSON blob.  This bypasses dynamic-variants swapping so we always get
  // the true selection — even when the combo is out of stock.
  function resolveVariantFromInputs() {
    var data = readProductData();
    if (!data || !data.variants) return null;

    var checked = document.querySelectorAll('input[data-variant-input]:checked');
    if (!checked.length) return null;

    var selected = {};
    checked.forEach(function(input) {
      var key = input.dataset.index; // e.g. "option1", "option2"
      if (key) selected[key] = input.value;
    });

    var match = data.variants.find(function(v) {
      return Object.keys(selected).every(function(key) {
        return v[key] === selected[key];
      });
    });

    return match ? match.id : null;
  }

  // Resolve a variant id from the color the viewer reports. The real viewer
  // always sends product_details.variantId === null (it has no knowledge of
  // Shopify variant ids) plus the chosen color name + index, so the page owns
  // the color -> variant mapping using the JSON blob's per-variant `color`.
  function resolveVariantFromColor(pd) {
    var data = readProductData();
    if (!data || !data.variants) return null;

    function matchByColorValue(value) {
      if (value == null) return null;
      var want = String(value).toLowerCase();
      var matches = data.variants.filter(function(v) {
        return v.color != null && String(v.color).toLowerCase() === want;
      });
      if (!matches.length) return null;
      var available = matches.find(function(v) { return v.available; });
      return (available || matches[0]).id;
    }

    // 1. Match on the color name the viewer reported (e.g. "Navy").
    var byName = matchByColorValue(pd.color);
    if (byName) return byName;

    // 2. Fall back to the color option value at the reported index.
    if (pd.colorIndex != null && data.colorOptionIndex >= 0 &&
        data.options && data.options[data.colorOptionIndex]) {
      var values = data.options[data.colorOptionIndex].values || [];
      var value = values[pd.colorIndex];
      // Newer Shopify serializes option values as objects ({ name: "Navy" }).
      if (value && typeof value === 'object') value = value.name;
      return matchByColorValue(value);
    }
    return null;
  }

  // Resolve honoring BOTH the viewer's reported color and the shopper's other
  // checked option selections (size etc.): the color option value is replaced
  // with the viewer's color, every other option group must still match the
  // PDP's checked inputs. Without this, a color-only match on a multi-option
  // product can add the right color in the wrong size.
  function resolveVariantFromInputsAndColor(pd) {
    var data = readProductData();
    if (!data || !data.variants || pd.color == null) return null;
    if (!(data.colorOptionIndex >= 0)) return null;

    var colorKey = 'option' + (data.colorOptionIndex + 1);
    var selected = {};
    document.querySelectorAll('input[data-variant-input]:checked').forEach(function(input) {
      var key = input.dataset.index; // e.g. "option1", "option2"
      if (key) selected[key] = input.value;
    });

    var want = String(pd.color).toLowerCase();
    var matches = data.variants.filter(function(v) {
      if (v[colorKey] == null || String(v[colorKey]).toLowerCase() !== want) return false;
      return Object.keys(selected).every(function(key) {
        return key === colorKey || v[key] === selected[key];
      });
    });
    if (!matches.length) return null;
    var available = matches.find(function(v) { return v.available; });
    return (available || matches[0]).id;
  }

  // Resolve a variant id straight from the PDP color swatches. These inputs are
  // always in the DOM (they carry data-variant-id), so this works even when the
  // JSON data blob is absent — which happens whenever the product's template
  // doesn't render it. Matches the viewer's reported color to a swatch value.
  function resolveVariantFromColorDom(pd) {
    if (pd.color == null) return null;
    var want = String(pd.color).toLowerCase();
    var inputs = document.querySelectorAll('input[data-variant-input][data-variant-id]');
    for (var i = 0; i < inputs.length; i++) {
      var val = inputs[i].value != null ? String(inputs[i].value).toLowerCase() : '';
      if (val === want) {
        var id = inputs[i].getAttribute('data-variant-id');
        if (id) return id;
      }
    }
    return null;
  }

  // Last-resort: the variant the PDP currently has selected.
  function resolveCheckedVariantDom() {
    var checked = document.querySelector('input[data-variant-input][data-variant-id]:checked');
    return checked ? checked.getAttribute('data-variant-id') : null;
  }

  // Build the payload sent to the iframe. variantId optionally overrides the
  // selectedVariantId baked into the JSON blob (used on variant:change).
  function buildPayload(variantId) {
    var data = readProductData();
    if (!data) return null;
    if (variantId != null) {
      data.selectedVariantId = variantId;
    }
    data.monogram.currentSelection = readMonogramSelection();
    var glbeCode = readGlobaleCurrency();
    if (glbeCode) {
      data.currency = {
        code: glbeCode,
        symbol: (data.currency && data.currency.symbol) || null,
        source: 'globale'
      };
    }
    return data;
  }

  function sendToIframe(type, payload) {
    if (!Bridge.iframeEl || !Bridge.iframeEl.contentWindow || !Bridge.targetOrigin) return;
    Bridge.iframeEl.contentWindow.postMessage({
      source: 'br-pdp',
      type: type,
      payload: payload
    }, Bridge.targetOrigin);
  }

  async function addToCart(items) {
    try {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      var body = await res.json();
      sendToIframe('pdp:add-to-cart-result', { ok: res.ok, response: body });
      if (res.ok) {
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    } catch (err) {
      sendToIframe('pdp:add-to-cart-result', { ok: false, error: String(err) });
    }
  }

  // Title-case a value so the viewer's lowercase monogram colour ("silver")
  // matches the native modal's option values ("Silver", "Gold").
  function titleCase(str) {
    return String(str).replace(/\w\S*/g, function(t) {
      return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    });
  }

  // Normalize the viewer's monogram object { text, style, colour } to the native
  // line-item property values, applying the monogram modal's validation
  // (letters/digits, max 3, uppercased). The viewer uses British "colour".
  function normalizeMonogram(mono) {
    if (!mono || typeof mono !== 'object') return null;
    if (typeof mono.text !== 'string' || mono.text.trim() === '') return null;

    var text = mono.text.trim().toUpperCase();
    if (!/^[A-Z0-9]{1,3}$/.test(text) || !mono.style) {
      sendToIframe('pdp:add-to-cart-result', {
        ok: false,
        error: 'invalid-monogram',
        detail: 'Monogram needs a style and 1-3 letters or numbers.'
      });
      return { invalid: true };
    }
    var colour = mono.colour != null ? mono.colour : mono.color;
    return {
      text: text,
      // Title-cased like colour: the viewer sends lowercase ("embossed"),
      // the native option values and the fee gate expect "Embossed".
      style: titleCase(mono.style),
      color: colour ? titleCase(colour) : null
    };
  }

  // Add the viewer's selection to the cart. `pd` is the viewer's product_details
  // { variantId, color, colorIndex, monogram }. variantId is always null from the
  // real viewer, so resolve it from the reported color, then fall back to the
  // PDP's current selection, then the default variant. The Color Foil fee never
  // displays inside the viewer (client decision) — it's charged via the fee
  // product line item and appears in the cart only.
  function addToCartFromViewer(pd) {
    var variantId = null;
    if (pd.variantId != null && !isNaN(parseInt(pd.variantId, 10))) {
      variantId = parseInt(pd.variantId, 10);
    }
    // The viewer never sends a Shopify variant id, so resolve it — preferring
    // the full-option match (viewer color + the PDP's other checked options),
    // then falling back to color-only matches: the PDP color swatches (always
    // in the DOM), then the JSON data blob, which only renders when the
    // product's template includes it.
    if (!variantId) {
      variantId = resolveVariantFromInputsAndColor(pd);
      if (!variantId && pd.color != null) {
        // On a multi-option product, an exact-combo miss means the viewer's color
        // isn't made in the shopper's selected size — refuse rather than silently
        // adding a different size via the color-only fallbacks below.
        //
        // Only when the color is one Shopify actually knows, though. The combo
        // match also misses when `pd.color` is an internal code the viewer uses
        // ("NVY") rather than the option value ("Navy"), and that case is exactly
        // what the colorIndex fallback below exists to rescue. Refusing on it
        // would make every viewer add-to-cart fail on a multi-option product and
        // leave that fallback unreachable.
        var exact = readProductData();
        var colorKnownToShopify = false;
        if (exact && exact.variants && exact.colorOptionIndex >= 0) {
          var exactKey = 'option' + (exact.colorOptionIndex + 1);
          var wantColor = String(pd.color).toLowerCase();
          colorKnownToShopify = exact.variants.some(function (v) {
            return v[exactKey] != null && String(v[exactKey]).toLowerCase() === wantColor;
          });
        }
        if (colorKnownToShopify && exact.options && exact.options.length > 1) {
          sendToIframe('pdp:add-to-cart-result', {
            ok: false,
            error: 'no-variant',
            detail: 'This color is not available in the selected combination.'
          });
          return;
        }
      }
    }
    if (!variantId) variantId = resolveVariantFromColorDom(pd);
    if (!variantId) variantId = resolveVariantFromColor(pd);
    if (!variantId) variantId = resolveVariantFromInputs();
    if (!variantId) variantId = resolveCheckedVariantDom();
    if (!variantId) {
      var initData = readProductData();
      variantId = initData && initData.selectedVariantId ? initData.selectedVariantId : null;
    }
    if (!variantId) {
      sendToIframe('pdp:add-to-cart-result', {
        ok: false,
        error: 'no-variant',
        detail: 'Could not resolve a variant for the viewer selection.'
      });
      return;
    }

    var items = [];
    var item = {
      id: parseInt(variantId, 10),
      quantity: pd.quantity ? parseInt(pd.quantity, 10) : 1
    };

    var mono = normalizeMonogram(pd.monogram);
    if (mono && mono.invalid) return;

    if (mono) {
      item.properties = {
        'Monogram': mono.text,
        'Monogram Style': mono.style
      };
      if (mono.color) {
        item.properties['Monogram Color'] = mono.color;
      }
    }
    items.push(item);

    if (mono && String(mono.style).toLowerCase() !== 'embossed') {
      var data = readProductData();
      var feeVariantId = data && data.monogram && data.monogram.feeProductVariantId;
      if (feeVariantId) {
        items.push({ id: parseInt(feeVariantId, 10), quantity: 1 });
      } else {
        console.warn('threedium: monogram fee product variant not configured; fee not added');
      }
    }

    addToCart(items);
  }

  // Click the PDP swatch for a variant id — this triggers the theme's existing
  // variantChange flow (theme.js:958-970), which dispatches `variant:change`.
  function selectVariantOnPdp(variantId) {
    if (!variantId) return;
    var input = document.querySelector(
      'input[data-variant-input][data-variant-id="' + String(variantId).replace(/"/g, '') + '"]'
    );
    if (input && !input.checked) input.click();
  }

  // Mirror the viewer's monogram config onto the PDP's native monogram fields so
  // the two stay in sync. Accepts { text, style, colour } (British "colour").
  function mirrorMonogramToPdp(update) {
    if (!update || typeof update !== 'object') return;
    if (typeof update.text === 'string') {
      document.querySelectorAll('.monogram__input-field').forEach(function(el) {
        el.value = update.text;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    if (update.style) {
      // Title-cased like the colour below, and for the same reason: the viewer sends
      // "embossed" while the native option value is "Embossed". Left raw, the selector
      // missed and the PDP's monogram radio silently stayed on the previous style
      // while the viewer showed the new one.
      var styleInput = document.querySelector(
        'input[name="monogram-style"][value="' + titleCase(update.style).replace(/"/g, '') + '"]'
      );
      if (styleInput && !styleInput.checked) styleInput.click();
    }
    var colour = update.colour != null ? update.colour : update.color;
    if (colour) {
      var titled = titleCase(colour).replace(/"/g, '');
      var colorInput = document.querySelector(
        'input[name="monogram-color"][value="' + titled + '"]'
      );
      if (colorInput && !colorInput.checked) colorInput.click();
    }
  }

  // Handle messages from the Threedium viewer. Verified against the live viewer
  // (briggs-riley-test.threedium.co.uk): real messages are tagged
  // `data_from: 'threedium'` with an `action` — NOT the source/type/payload
  // shape the bridge was originally written against (that shape was never
  // emitted by the real viewer, so add-to-cart silently no-op'd). Observed:
  //   { action: 'add_to_cart', product_details: { variantId, color, colorIndex, monogram } }
  //   { action: 'change_configuration', monogram: { text, style, colour } }   // monogram edit
  //   { action: 'change_configuration', sku: 'Navy' }                          // product-color change
  // product_details.variantId is always null; the page resolves it from color.
  function handleIframeMessage(event) {
    if (event.origin !== Bridge.targetOrigin) return;
    if (!isTrustedViewerOrigin(event.origin)) return; // defense in depth
    var data = event.data;
    if (!data || data.data_from !== 'threedium' || !data.action) return;

    switch (data.action) {
      case 'add_to_cart':
        addToCartFromViewer(data.product_details || {});
        break;

      case 'change_configuration':
        // Live-sync the viewer's configuration back onto the PDP.
        if (data.monogram) mirrorMonogramToPdp(data.monogram);
        // A product-color change arrives as `sku` holding the color value
        // (e.g. "Navy"); alternate shapes may use color/colorIndex.
        var colorRef = null;
        if (data.sku != null) colorRef = { color: data.sku };
        else if (data.color != null || data.colorIndex != null) colorRef = data;
        if (colorRef) {
          var vid = resolveVariantFromColorDom(colorRef) || resolveVariantFromColor(colorRef);
          if (vid) selectVariantOnPdp(vid);
        }
        break;
    }
  }

  function createModal() {
    var modal = document.createElement('div');
    modal.id = 'iframeModal';
    modal.style.display = 'none';
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.right = '0';
    modal.style.bottom = '0';
    modal.style.background = 'rgba(0, 0, 0, 0.3)';
    modal.style.zIndex = '9999';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';

    var content = document.createElement('div');
    content.className = 'modal-content';
    content.style.width = window.innerWidth <= 768 ? '100%' : '82%';
    content.style.height = window.innerWidth <= 768 ? '100%' : '83%';
    content.style.background = '#fff';
    content.style.position = 'relative';
    content.style.borderRadius = '8px';
    content.style.overflow = 'hidden';

    var iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.borderWidth = '0';

    content.appendChild(iframe);
    modal.appendChild(content);

    var closeButton = document.createElement('div');
    closeButton.innerHTML = `
      <svg class="close" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M16 0.96L15.06 0.02L8.00667 7.06L0.94 0L0 0.933333L7.06667 8L0 15.0667L0.946667 16L8.00667 8.94667L15.06 15.9867L16 15.0533L8.95333 8L16 0.96Z" fill="black"/>
      </svg>
    `;
    closeButton.className = 'modal-close';
    closeButton.style.cursor = 'pointer';
    closeButton.style.position = 'absolute';
    closeButton.style.top = '24px';
    closeButton.style.right = '24px';
    closeButton.style.color = 'black';
    closeButton.style.zIndex = '10';
    closeButton.style.width = '32px';
    closeButton.style.height = '32px';
    closeButton.style.display = 'flex';
    closeButton.style.alignItems = 'center';
    closeButton.style.justifyContent = 'center';
    closeButton.style.background = 'rgba(255, 255, 255, 0.9)';
    closeButton.style.borderRadius = '50%';

    content.appendChild(closeButton);

    // Close modal when clicking the close button
    closeButton.addEventListener('click', function() {
      closeModal({modal: modal, iframe: iframe});
    });

    // Close modal when clicking outside the content area
    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        closeModal({modal: modal, iframe: iframe});
      }
    });

    // Close modal with Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display === 'flex') {
        closeModal({modal: modal, iframe: iframe});
      }
    });

    document.body.appendChild(modal);

    return {
      modal: modal,
      iframe: iframe,
      content: content
    };
  }

  function bindButtonEvents(button, modal) {
    var iframeSrc = button.getAttribute('data-iframe-src');
    
    if (!iframeSrc) {
      console.warn('3D button found but no data-iframe-src attribute');
      return;
    }

    // Handle both click and touch events to prevent carousel interference
    button.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get product title for analytics
      var productTitle = getProductTitle();
      
      // Open the modal
      openModal(modal, iframeSrc);
      
      // Track the event
      track3DClick(productTitle);
    });

    // Prevent carousel touch events from interfering with button clicks
    button.addEventListener('touchstart', function(e) {
      e.stopPropagation();
    }, { passive: true });

    button.addEventListener('touchend', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get product title for analytics
      var productTitle = getProductTitle();
      
      // Open the modal
      openModal(modal, iframeSrc);
      
      // Track the event
      track3DClick(productTitle);
    }, { passive: false });

    // Prevent touchmove from interfering
    button.addEventListener('touchmove', function(e) {
      e.stopPropagation();
    }, { passive: true });
  }

  // Only the Threedium-hosted viewer is trusted to exchange product data and
  // drive the cart. The iframe URL comes from a merchant-managed metafield
  // (product.metafields.custom.iframe_url); a mis-set or hijacked value pointing
  // anywhere else must NOT receive the product/monogram payload or be able to
  // add to the cart. Confirm this list covers the production viewer host.
  var TRUSTED_VIEWER_HOSTS = ['threedium.co.uk', 'threedium.io', 'unlimited3d.com'];
  function isTrustedViewerOrigin(origin) {
    try {
      var host = new URL(origin).hostname.toLowerCase();
      return TRUSTED_VIEWER_HOSTS.some(function(base) {
        return host === base || host.endsWith('.' + base);
      });
    } catch (e) {
      return false;
    }
  }

  function openModal(modalElement, url) {
    var origin = null;
    try {
      origin = new URL(url, window.location.href).origin;
    } catch (err) {
      origin = null;
    }

    // The URL comes from the merchant-editable `custom.iframe_url` metafield, and a
    // hostile value has been found in this store before. Refuse before loading it:
    // an untrusted origin is never framed at all, so it cannot render in the
    // shopper's session, and a `javascript:` value — which has no origin and fails
    // this check — never reaches the src attribute where it would execute in the
    // storefront's own origin. Disabling only the message bridge, as this used to,
    // left the hostile document loaded.
    if (!origin || !isTrustedViewerOrigin(origin)) {
      console.warn('threedium: iframe origin not trusted; refusing to load', origin);
      Bridge.targetOrigin = null;
      Bridge.iframeEl = null;
      return;
    }

    modalElement.iframe.src = url;
    modalElement.modal.style.display = 'flex';

    // Prevent body scroll when modal is open. The class is a styling hook for
    // page chrome that must yield to the viewer (the sticky ATC bar hides on it).
    document.body.style.overflow = 'hidden';
    document.body.classList.add('threedium-modal-open');

    Bridge.targetOrigin = origin;
    Bridge.iframeEl = modalElement.iframe;

    Bridge.messageHandler = handleIframeMessage;
    window.addEventListener('message', Bridge.messageHandler);

    // Re-sync iframe whenever the PDP variant changes.
    Bridge.variantChangeHandler = function() {
      sendToIframe('pdp:init', buildPayload(resolveVariantFromInputs()));
    };
    document.addEventListener('variant:change', Bridge.variantChangeHandler);

    // Send initial payload as soon as the iframe document loads.
    modalElement.iframe.addEventListener('load', function onLoad() {
      modalElement.iframe.removeEventListener('load', onLoad);
      sendToIframe('pdp:init', buildPayload(resolveVariantFromInputs()));
    });
  }

  function closeModal(modalObj) {
    modalObj.modal.style.display = 'none';
    modalObj.iframe.src = '';

    // Restore body scroll
    document.body.style.overflow = '';
    document.body.classList.remove('threedium-modal-open');

    // Tear down the bridge so listeners don't accumulate across opens.
    if (Bridge.messageHandler) {
      window.removeEventListener('message', Bridge.messageHandler);
      Bridge.messageHandler = null;
    }
    if (Bridge.variantChangeHandler) {
      document.removeEventListener('variant:change', Bridge.variantChangeHandler);
      Bridge.variantChangeHandler = null;
    }
    Bridge.targetOrigin = null;
    Bridge.iframeEl = null;
  }

  function getProductTitle() {
    // Try to get product title from various sources
    var titleElement = document.querySelector('[data-product-title]') ||
                      document.querySelector('.product__title') ||
                      document.querySelector('h1.product-single__title') ||
                      document.querySelector('h1');
    
    return titleElement ? titleElement.textContent.trim() : 'Product';
  }

  function track3DClick(productTitle) {
    // Google Analytics tracking
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        'event': '3D_click',
        'product': productTitle
      });
    }
    
    // Also track with gtag if available
    if (typeof gtag !== 'undefined') {
      gtag('event', '3D_click', {
        'product_name': productTitle
      });
    }
  }

})();
